export interface MultiComboboxOption {
  id: number;
  name: string;
}

export interface TeamRow {
  id: number;
  name: string;
  region: string;
  department: string;
  syncore_ref: string | null;
  active: boolean;
}

export interface RawTeamGroup {
  /** Canonical warehouse team id (used in URL scope params). */
  id: number;
  name: string;
  teamIds: number[];
}

function groupKey(team: TeamRow): string {
  return team.syncore_ref ?? `id:${team.id}`;
}

function sortTeamRows(a: TeamRow, b: TeamRow): number {
  if (a.region !== b.region) {
    if (a.region === "CAN") return -1;
    if (b.region === "CAN") return 1;
  }
  return a.name.localeCompare(b.name);
}

/** Merge CAN + US warehouse rows that share the same Syncore ref into one picker entry. */
export function buildRawTeamGroups(teams: TeamRow[]): RawTeamGroup[] {
  const grouped = new Map<string, TeamRow[]>();
  for (const team of teams) {
    const key = groupKey(team);
    const members = grouped.get(key) ?? [];
    members.push(team);
    grouped.set(key, members);
  }

  return Array.from(grouped.values())
    .map((members) => {
      const sorted = [...members].sort(sortTeamRows);
      const canonical = sorted[0]!;
      return {
        id: canonical.id,
        name: canonical.name,
        teamIds: sorted.map((team) => team.id),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function teamOptionsFromGroups(groups: RawTeamGroup[]): MultiComboboxOption[] {
  return groups.map((group) => ({ id: group.id, name: group.name }));
}

export function expandRawTeamSelection(selectedIds: number[], groups: RawTeamGroup[]): number[] {
  if (selectedIds.length === 0) return [];

  const expanded = new Set<number>();
  for (const id of selectedIds) {
    const group = groups.find((candidate) => candidate.id === id);
    if (group) {
      for (const teamId of group.teamIds) expanded.add(teamId);
    } else {
      expanded.add(id);
    }
  }
  return [...expanded];
}
